import * as THREE from 'three';
import { MeshLambertNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { TexturePath } from '../../../utils/serialization/decorator.js';
import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** WebGPU `MeshLambertNodeMaterial` asset: Lambert diffuse shading with authored color. */
@EngineClass('MeshLambertNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Mesh Lambert Node Material',
  nodeMaterialGroup: 'TSL Node (THREE)',
})
export class MeshLambertNodeMaterialAsset extends NodeMaterialAsset(MeshLambertNodeMaterial) {

  @property({ type: 'color', description: 'Diffuse color' })
  override color = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'color', description: 'Emissive color' })
  override emissive = new THREE.Color(0.0, 0.0, 0.0);

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Emissive intensity' })
  override emissiveIntensity = 1.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Ambient occlusion map intensity' })
  override aoMapIntensity = 1.0;

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Light map intensity' })
  override lightMapIntensity = 1.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Environment reflectivity' })
  override reflectivity = 1.0;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, description: 'Refraction ratio' })
  override refractionRatio = 0.98;

  @property({ type: 'number', min: 0, max: 10, step: 0.1, description: 'Environment map intensity' })
  override envMapIntensity = 1.0;

  @property({ type: 'texturePath', description: 'Color map path' })
    mapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Alpha map path' })
    alphaMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Emissive map path' })
    emissiveMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Environment map path' })
    envMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Light map path' })
    lightMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Ambient occlusion map path' })
    aoMapPath: TexturePath = '';

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.emissiveIntensity = this.floorFiniteNumber(this.emissiveIntensity, 0, 1.0);
    this.aoMapIntensity = this.clampFiniteNumber(this.aoMapIntensity, 0, 1, 1.0);
    this.lightMapIntensity = this.floorFiniteNumber(this.lightMapIntensity, 0, 1.0);
    this.reflectivity = this.clampFiniteNumber(this.reflectivity, 0, 1, 1.0);
    this.refractionRatio = this.clampFiniteNumber(this.refractionRatio, 0, 1, 0.98);
    this.envMapIntensity = this.floorFiniteNumber(this.envMapIntensity, 0, 1.0);
    this.syncTexturePath('map', this.mapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('alphaMap', this.alphaMapPath, THREE.NoColorSpace);
    this.syncTexturePath('emissiveMap', this.emissiveMapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('envMap', this.envMapPath, THREE.NoColorSpace);
    this.syncTexturePath('lightMap', this.lightMapPath, THREE.NoColorSpace);
    this.syncTexturePath('aoMap', this.aoMapPath, THREE.NoColorSpace);
    this.colorNode = null;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): MeshLambertNodeMaterialAsset {
    const instance = new MeshLambertNodeMaterialAsset();
    MeshLambertNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
